import editHistoryService from '../services/EditHistoryService';
import uuidV4 from 'uuid/v4';
import find from 'lodash/find';
import reject from 'lodash/reject';
import isEmpty from 'lodash/isEmpty';

const UNDO_LINK_TIMEOUT = 60000;
export const NEW_EDIT = 'NEW_EDIT';

export const HISTORY_PERSIST_REQUEST = 'HISTORY_PERSIST_REQUEST';
export const HISTORY_PERSIST_SUCCESS = 'HISTORY_PERSIST_SUCCESS';
export const HISTORY_PERSIST_FAILURE = 'HISTORY_PERSIST_FAILURE';

export const onEdit = (id, historyItem) => async (dispatch) => {
    const uuid = uuidV4();
    historyItem = {...historyItem, imageId: id, uuid, dateCreated: new Date().getMilliseconds()};
    dispatch({type: NEW_EDIT, id, historyItem});
    const payload = {
        ...historyItem,
        imageId: id,
        uuid,
        path: JSON.stringify(historyItem.path),
        historyProps: JSON.stringify(historyItem.historyProps)
    };
    dispatch({type: HISTORY_PERSIST_REQUEST});
    try {
        await editHistoryService.persistEdit(payload);
        return dispatch({type: HISTORY_PERSIST_SUCCESS, id});
    } catch (error) {
        console.error('error persisting history', error);
        return dispatch({type: HISTORY_PERSIST_FAILURE, id, error, errorNotification: 'error persisting edits'});
    }
};

export const HISTORY_ITEM_VISIBLE_CHANGE = 'HISTORY_ITEM_VISIBLE_CHANGE';

export const onHistoryToggleVisible = (uuid) => async (dispatch, getState) => {
    const {editHistory, openImage: {id}} = getState();
    if (!editHistory[id]) {
        return null;
    }
    const itemToToggle = find(editHistory[id].edits, ['uuid', uuid]);
    if (!itemToToggle) {
        return null;
    }
    dispatch({type: HISTORY_ITEM_VISIBLE_CHANGE, id, uuid});
    try {
        await editHistoryService.persistHidden(!itemToToggle.hidden, [uuid]);
        return dispatch({type: HISTORY_PERSIST_SUCCESS, id});
    } catch (error) {
        return dispatch({type: HISTORY_PERSIST_FAILURE, id, error, errorNotification: 'error persisting hide/show toggle'});
    }
};

export const PERMANENT_DELETE_HISTORY_ITEM = 'PERMANENT_DELETE_HISTORY_ITEM';

export const onPermanentDeleteHistoryItem = (uuid) => async (dispatch, getState) => {
    const {openImage: {id}} = getState();
    dispatch({type: PERMANENT_DELETE_HISTORY_ITEM, id, uuid});

    dispatch({type: HISTORY_PERSIST_REQUEST});
    try {
        await editHistoryService.persistDelete(true, true, [uuid]);
        return dispatch({type: HISTORY_PERSIST_SUCCESS, id});
    } catch (error) {
        console.error('error persisting history', error);
        return dispatch({type: HISTORY_PERSIST_FAILURE, id, error, errorNotification: 'error persisting permanent delete'});
    }
};

export const DELETE_HISTORY_ITEM = 'DELETE_HISTORY_ITEM';

export const onDeleteHistoryItem = (uuid) => async (dispatch, getState) => {
    const {openImage: {id}} = getState();
    const timeoutId = setTimeout(() => {
        onPermanentDeleteHistoryItem(uuid)(dispatch, getState);
    }, UNDO_LINK_TIMEOUT);

    dispatch({type: DELETE_HISTORY_ITEM, id, uuid, timeoutId});

    dispatch({type: HISTORY_PERSIST_REQUEST});
    try {
        await editHistoryService.persistDelete(true, false, [uuid]);
        return dispatch({type: HISTORY_PERSIST_SUCCESS, id});
    } catch (error) {
        console.error('error persisting history', error);
        return dispatch({type: HISTORY_PERSIST_FAILURE, id, error, errorNotification: 'error persisting delete'});
    }
};

export const UNDO_DELETE_HISTORY_ITEM = 'UNDO_DELETE_HISTORY_ITEM';

export const onUndoDeleteHistoryItem = () => async (dispatch, getState) => {
    const {editHistory, openImage: {id}} = getState();
    if (!editHistory[id]) {
        return null;
    }
    const {lastDelete} = editHistory[id];
    if (!lastDelete) {
        return null;
    }
    dispatch({type: UNDO_DELETE_HISTORY_ITEM, id, uuid: lastDelete});

    dispatch({type: HISTORY_PERSIST_REQUEST});
    try {
        await editHistoryService.persistDelete(false, false, [lastDelete]);
        return dispatch({type: HISTORY_PERSIST_SUCCESS, id});
    } catch (error) {
        console.error('error persisting history', error);
        return dispatch({type: HISTORY_PERSIST_FAILURE, id, error, errorNotification: 'error persisting canceling delete'});
    }
};

export const PERMANENT_RESET_HISTORY = 'PERMANENT_RESET_HISTORY';

export const onPermanentResetHistory = (uuids) => async (dispatch, getState) => {
    const {openImage: {id}} = getState();
    dispatch({type: PERMANENT_RESET_HISTORY, id, uuids});

    dispatch({type: HISTORY_PERSIST_REQUEST});
    try {
        await editHistoryService.persistDelete(true, true, uuids);
        return dispatch({type: HISTORY_PERSIST_SUCCESS, id});
    } catch (error) {
        console.error('error persisting history', error);
        return dispatch({type: HISTORY_PERSIST_FAILURE, id, error, errorNotification: 'error persisting anceling permanent delete'});
    }
};

export const RESET_HISTORY = 'RESET_HISTORY';

export const onResetHistory = () => async (dispatch, getState) => {
    const {editHistory, openImage: {id}} = getState();
    if (!editHistory[id]) {
        return null;
    }
    const {edits} = editHistory[id];
    const uuids = reject(edits, 'deleted').map(edit => edit.uuid);
    const timeoutId = setTimeout(() => {
        onPermanentResetHistory(uuids)(dispatch, getState);
    }, UNDO_LINK_TIMEOUT);

    dispatch({type: RESET_HISTORY, id, uuids, timeoutId});

    dispatch({type: HISTORY_PERSIST_REQUEST});
    try {
        await editHistoryService.persistDelete(true, false, uuids);
        return dispatch({type: HISTORY_PERSIST_SUCCESS, id});
    } catch (error) {
        console.error('error persisting history', error);
        return dispatch({type: HISTORY_PERSIST_FAILURE, id, error, errorNotification: 'error persisting reset history'});
    }
};

export const UNDO_RESET_HISTORY = 'UNDO_RESET_HISTORY';

export const onUndoResetHistory = () => async (dispatch, getState) => {
    const {editHistory, openImage: {id}} = getState();
    if (!editHistory[id]) {
        return null;
    }
    const {lastReset} = editHistory[id];
    if (isEmpty(lastReset)) {
        return null;
    }
    dispatch({type: UNDO_RESET_HISTORY, id});

    dispatch({type: HISTORY_PERSIST_REQUEST});
    try {
        await editHistoryService.persistDelete(false, false, lastReset);
        return dispatch({type: HISTORY_PERSIST_SUCCESS, id});
    } catch (error) {
        console.error('error persisting history', error);
        return dispatch({type: HISTORY_PERSIST_FAILURE, id, error, errorNotification: 'error persisting undo reset history'});
    }
};

export const HISTORY_ITEM_HIGHLIGHT = 'HISTORY_ITEM_HIGHLIGHT';

export const onHistoryHighlightItem = (uuid, on) => (dispatch, getState) => {
    const {openImage: {id}} = getState();
    return dispatch({type: HISTORY_ITEM_HIGHLIGHT, id, uuid, on});
};



// WEBPACK FOOTER //
// ./src/actions/EditHistoryActions.js